// Scroll-Frame-Profiler: misst pro RAF-Frame die Zeit in den einzelnen
// Render-Sektionen und loggt Frames mit Budget-Overrun (>16.67 ms) auffällig.
//
// Ziel: Identifizieren, ob der "Separator-Hüpfer" tatsächlich aus RAF-Drops
// wegen teurer Sektionen kommt (Hypothese: Border-Rerender + Outline-Full
// + Context-Shift summieren sich beim Separator-Durchgang über das Budget).
//
// Aktivieren (in DevTools Console):
//   Debug.scrollTrace = true         // Alle Scroll-relevanten Frames
//   Debug.scrollTraceAll = true      // JEDES Frame loggen (sehr viel Output)
//   Debug.scrollTrace = false        // Wieder aus
//
// Output-Format:
//   [scroll#123] path=shift elapsed=18.3ms ⚠ BUDGET |
//     sync=0.2 shift.blit=0.8 shift.part=1.2 outline=3.4 ctx=2.1 bord=7.8 curs=0.3 |
//     sp=4 bd=true sec="4.2" vps=45 vpo=0.31 xd=4.31 wheel=2
//
// Legende:
//   sp   = shiftPixels (gerundeter Pixel-Shift)
//   bd   = borderDirty vor dem Shift-Pfad (true = Border wird neu gezeichnet)
//   sec  = Separator-Label über dem Viewport (wechselt bei Separator-Durchgang)
//   vps  = viewportStart (Zeilen-Index)
//   vpo  = _viewportPixelOffset (Sub-Zeilen-Offset in CSS-px)
//   xd   = exactDelta (_scrollPixelDelta + _shiftResidual, vor Rundung)
//   wheel/touch = Anzahl Input-Events in diesem Frame
//   wLag = Wheel-Event-Delivery-Latency (performance.now() - event.timeStamp)
//          als min/avg/max in ms. Hohe Werte (>16) = Browser staut Events.
//   wHt  = Summierte Wheel-Handler-JS-Zeit (Hit-Test + scrollByPixels) in ms
//   tLag/tHt = analoge Metriken für Touch-Move-Events

const FRAME_BUDGET_MS = 16.67

class ScrollProfiler {
    constructor() {
        this._frameNum = 0
        this._frameT0 = 0
        this._lastMarkT = 0
        this._marks = []
        this._wheelEventsInFrame = 0
        this._touchEventsInFrame = 0
        this._inertiaTicksInFrame = 0
        // Pro-Event-Messungen (Arrays werden pro Frame ausgewertet & geleert)
        this._wheelLags = []
        this._wheelHandlerMs = 0
        this._wheelIntervals = []     // Inter-Arrival-Zeit in ms (Event N zu Event N-1)
        this._lastWheelT = 0
        this._touchLags = []
        this._touchHandlerMs = 0
        this._touchIntervals = []
        this._lastTouchT = 0
        this._enabled = false
        this._enabledAll = false
        this._lastFrameEndT = 0
    }

    frameStart() {
        // Flag einmal pro Frame cachen — window-Lookup im mark()-Hotpath vermeiden
        this._enabled = !!(typeof window !== 'undefined' && window.Debug && window.Debug.scrollTrace)
        this._enabledAll = !!(typeof window !== 'undefined' && window.Debug && window.Debug.scrollTraceAll)
        if (!this._enabled && !this._enabledAll) return
        this._frameNum++
        const now = performance.now()
        // RAF-Gap: Zeit seit dem letzten frameEnd. Wert >17 ms ≈ RAF-Drop vom Browser.
        this._rafGapMs = this._lastFrameEndT ? now - this._lastFrameEndT : 0
        this._frameT0 = now
        this._lastMarkT = now
        this._marks.length = 0
    }

    mark(name) {
        if (!this._enabled && !this._enabledAll) return
        const now = performance.now()
        this._marks.push({ name, dtMs: now - this._lastMarkT })
        this._lastMarkT = now
    }

    /**
     * Wheel-Event-Metriken sammeln.
     * @param {number} [lagMs] - performance.now() - event.timeStamp (Event-Stau-Indikator)
     * @param {number} [handlerMs] - Zeit die der Wheel-Handler im JS verbracht hat
     */
    noteWheel(lagMs, handlerMs) {
        if (!this._enabled && !this._enabledAll) return
        this._wheelEventsInFrame++
        const now = performance.now()
        if (this._lastWheelT > 0) {
            this._wheelIntervals.push(now - this._lastWheelT)
        }
        this._lastWheelT = now
        if (typeof lagMs === 'number' && isFinite(lagMs) && lagMs >= 0) {
            this._wheelLags.push(lagMs)
        }
        if (typeof handlerMs === 'number' && isFinite(handlerMs)) {
            this._wheelHandlerMs += handlerMs
        }
    }

    /**
     * Touch-Move-Event-Metriken sammeln (analog zu noteWheel).
     */
    noteTouch(lagMs, handlerMs) {
        if (!this._enabled && !this._enabledAll) return
        this._touchEventsInFrame++
        const now = performance.now()
        if (this._lastTouchT > 0) {
            this._touchIntervals.push(now - this._lastTouchT)
        }
        this._lastTouchT = now
        if (typeof lagMs === 'number' && isFinite(lagMs) && lagMs >= 0) {
            this._touchLags.push(lagMs)
        }
        if (typeof handlerMs === 'number' && isFinite(handlerMs)) {
            this._touchHandlerMs += handlerMs
        }
    }

    noteInertiaTick() {
        if (!this._enabled && !this._enabledAll) return
        this._inertiaTicksInFrame++
    }

    /**
     * Schließt einen Frame ab und entscheidet ob geloggt wird.
     * @param {string} path - 'shift' | 'full' | 'partial' | 'cursor-only' | 'blink' | 'idle'
     * @param {Object} [metadata] - zusätzliche Felder (sp, bd, vps, vpo, sec, xd)
     */
    frameEnd(path, metadata = {}) {
        if (!this._enabled && !this._enabledAll) {
            this._lastFrameEndT = 0
            return
        }
        if (!this._frameT0) return

        const elapsedMs = performance.now() - this._frameT0
        const overrun = elapsedMs > FRAME_BUDGET_MS
        const rafGap = this._rafGapMs || 0
        const rafDropped = rafGap > FRAME_BUDGET_MS * 1.3  // grobes Kriterium: Gap > ~21 ms

        // Loggen wenn:
        // - scrollTraceAll: immer
        // - scrollTrace: Scroll-aktive Frames + Budget-Overruns + Border-Wechsel + RAF-Drops
        const worthLogging = this._enabledAll
            || overrun
            || rafDropped
            || path === 'shift'
            || path === 'partial'
            || (metadata.bd)
            || (this._wheelEventsInFrame > 0)
            || (this._touchEventsInFrame > 0)
            || (this._inertiaTicksInFrame > 0)

        if (worthLogging) {
            const parts = []
            parts.push(`[scroll#${this._frameNum}]`)
            parts.push(`path=${path}`)
            parts.push(`elapsed=${elapsedMs.toFixed(1)}ms`)
            if (overrun) parts.push('⚠BUDGET')
            if (rafDropped) parts.push(`⚠RAFgap=${rafGap.toFixed(0)}ms`)

            if (this._marks.length) {
                const compStr = this._marks.map(m => `${m.name}=${m.dtMs.toFixed(1)}`).join(' ')
                parts.push('|', compStr)
            }

            const metaStr = Object.entries(metadata)
                .filter(([_, v]) => v !== undefined && v !== null && v !== '')
                .map(([k, v]) => typeof v === 'number' ? `${k}=${v.toFixed ? v.toFixed(2) : v}` : `${k}=${v}`)
                .join(' ')
            if (metaStr) parts.push('|', metaStr)

            if (this._wheelEventsInFrame) parts.push(`wheel=${this._wheelEventsInFrame}`)
            if (this._touchEventsInFrame) parts.push(`touch=${this._touchEventsInFrame}`)
            if (this._inertiaTicksInFrame) parts.push(`inertia=${this._inertiaTicksInFrame}`)

            // Event-Latency & Handler-Zeit — nur wenn tatsächlich Daten da
            if (this._wheelLags.length) {
                const lags = this._wheelLags
                let mn = Infinity, mx = -Infinity, sum = 0
                for (const v of lags) { if (v < mn) mn = v; if (v > mx) mx = v; sum += v }
                const avg = sum / lags.length
                parts.push(`wLag=${mn.toFixed(0)}/${avg.toFixed(0)}/${mx.toFixed(0)}`)
            }
            if (this._wheelIntervals.length) {
                const iv = this._wheelIntervals
                let mn = Infinity, mx = -Infinity, sum = 0
                for (const v of iv) { if (v < mn) mn = v; if (v > mx) mx = v; sum += v }
                const avg = sum / iv.length
                parts.push(`wInter=${mn.toFixed(0)}/${avg.toFixed(0)}/${mx.toFixed(0)}`)
            }
            if (this._wheelHandlerMs > 0) parts.push(`wHt=${this._wheelHandlerMs.toFixed(1)}`)
            if (this._touchLags.length) {
                const lags = this._touchLags
                let mn = Infinity, mx = -Infinity, sum = 0
                for (const v of lags) { if (v < mn) mn = v; if (v > mx) mx = v; sum += v }
                const avg = sum / lags.length
                parts.push(`tLag=${mn.toFixed(0)}/${avg.toFixed(0)}/${mx.toFixed(0)}`)
            }
            if (this._touchIntervals.length) {
                const iv = this._touchIntervals
                let mn = Infinity, mx = -Infinity, sum = 0
                for (const v of iv) { if (v < mn) mn = v; if (v > mx) mx = v; sum += v }
                const avg = sum / iv.length
                parts.push(`tInter=${mn.toFixed(0)}/${avg.toFixed(0)}/${mx.toFixed(0)}`)
            }
            if (this._touchHandlerMs > 0) parts.push(`tHt=${this._touchHandlerMs.toFixed(1)}`)

            // Overrun rot, RAF-Drop orange, Shift-Pfad blau, Full-Render grau, sonst default
            const style = overrun ? 'color:#ff5555;font-weight:bold'
                : rafDropped ? 'color:#ffa500;font-weight:bold'
                : path === 'shift' ? 'color:#8ab4f8'
                : path === 'full' ? 'color:#888'
                : 'color:inherit'
            console.log('%c' + parts.join(' '), style)
        }

        this._lastFrameEndT = performance.now()
        this._frameT0 = 0
        this._wheelEventsInFrame = 0
        this._touchEventsInFrame = 0
        this._inertiaTicksInFrame = 0
        // Pro-Event-Arrays leeren (in-place, keine Neuallokation).
        // Inter-Arrival-Timestamps resetten: der erste Event des nächsten Frames
        // soll kein Interval zum letzten Event des vorherigen Frames berechnen
        // (das wäre die Cross-Frame-RAF-Gap-Zeit, nicht das Event-Sampling-Intervall).
        this._wheelLags.length = 0
        this._touchLags.length = 0
        this._wheelIntervals.length = 0
        this._touchIntervals.length = 0
        this._wheelHandlerMs = 0
        this._touchHandlerMs = 0
        this._lastWheelT = 0
        this._lastTouchT = 0
    }
}

export const scrollProfiler = new ScrollProfiler()

// Toggle-Hook global bereitstellen (Boris kann in Console umschalten)
if (typeof window !== 'undefined') {
    window.Debug = window.Debug || {}
    if (window.Debug.scrollTraceAll === undefined) window.Debug.scrollTraceAll = false

    // Main-Thread-Heartbeat: setTimeout(33ms) loggt, wenn der Abstand zum letzten
    // Tick signifikant über dem Soll liegt. Zweck: unterscheiden zwischen
    //   (a) Main-Thread blockiert (Timer pausiert auch) → JS-Problem
    //   (b) Main-Thread läuft, aber RAF wird nicht gefeuert → Compositor/Event-Priorität
    //
    // (a) bedeutet: unser Code (oder Browser-Intern) hält den Main-Thread.
    // (b) ist das klassische "Wheel-Events trumpfen RAF"-Muster auf macOS/Safari.
    //
    // Der Timer läuft NUR bei aktivem Debug.scrollTrace (System-Review
    // 2026-06-10, Kleinigkeit 19 — vorher tickte er permanent in Produktion).
    // Debug.scrollTrace ist deshalb ein Accessor: Einschalten in der Console
    // armiert den Heartbeat, Ausschalten lässt ihn beim nächsten Tick sterben.
    const HEARTBEAT_MS = 33
    const HEARTBEAT_LOG_THRESHOLD_MS = 80  // ab hier interessant (>2 Frames Gap)
    let _hbLast = performance.now()
    let _hbArmed = false
    const _hbTick = () => {
        if (!window.Debug.scrollTrace) {
            _hbArmed = false
            return
        }
        const now = performance.now()
        const gap = now - _hbLast
        if (gap > HEARTBEAT_LOG_THRESHOLD_MS) {
            console.log(
                `%c[heartbeat] main-thread-gap=${gap.toFixed(0)}ms (soll ~${HEARTBEAT_MS}ms) — Main-Thread war ${(gap - HEARTBEAT_MS).toFixed(0)}ms länger idle als erwartet`,
                'color:#ff9500;font-weight:bold'
            )
        }
        _hbLast = now
        setTimeout(_hbTick, HEARTBEAT_MS)
    }

    let _scrollTrace = window.Debug.scrollTrace === true
    Object.defineProperty(window.Debug, 'scrollTrace', {
        configurable: true,
        get() { return _scrollTrace },
        set(v) {
            _scrollTrace = !!v
            if (_scrollTrace && !_hbArmed) {
                _hbArmed = true
                _hbLast = performance.now()
                setTimeout(_hbTick, HEARTBEAT_MS)
            }
        }
    })
}
